import React from 'react';
import { getLanguage } from '@deadpan/shared';
import { useTranslations } from '../hooks/useTranslations';
import { DrawLots } from './DrawLots';
import { Pensieve } from './Pensieve';

type ToolsTab = 'drawLots' | 'pensieve';

interface ToolsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const DRAW_LOTS_RULES_ZH = {
  title: '使用规则',
  rule1: '每行输入一个选项。',
  rule2: '点「抽签」随机抽一个。',
};
const DRAW_LOTS_RULES_EN = {
  title: 'How to use',
  rule1: 'One option per line.',
  rule2: 'Click "Draw" to pick one.',
};
const PENSIEVE_RULES_ZH = {
  rule1: '选择一个等级，等级越高，深度越深。',
};
const PENSIEVE_RULES_EN = {
  rule1: 'Select a level, the depth deepens as level goes up.',
};

export const ToolsPanel: React.FC<ToolsPanelProps> = ({ isOpen, onClose }) => {
  const t = useTranslations();
  const [selectedTab, setSelectedTab] = React.useState<ToolsTab>('drawLots');
  const isZh = getLanguage() === 'zh';
  const rulesFallback = isZh ? DRAW_LOTS_RULES_ZH : DRAW_LOTS_RULES_EN;

  if (!isOpen) return null;

  const tabs: { id: ToolsTab; label: string }[] = [
    { id: 'drawLots', label: t.ui.drawLotsTab ?? (isZh ? '抽签' : 'Draw Lots') },
    { id: 'pensieve', label: t.ui.pensieveTab ?? (isZh ? '冥想盆' : 'Pensieve') },
  ];
  
  const getRulesForTab = (tab: ToolsTab) => {
    if (tab === 'pensieve') {
      const pensieveRulesFallback = isZh ? PENSIEVE_RULES_ZH : PENSIEVE_RULES_EN;
      return {
        rule1: t.ui.pensieveRule1 ?? pensieveRulesFallback.rule1,
      };
    }
    return {
      title: t.ui.drawLotsRulesTitle ?? rulesFallback.title,
      rule1: t.ui.drawLotsRule1 ?? rulesFallback.rule1,
      rule2: t.ui.drawLotsRule2 ?? rulesFallback.rule2,
    };
  };
  
  const currentRules = getRulesForTab(selectedTab);

  return (
    <>
      <div
        className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-4"
        role="dialog"
        aria-label={t.ui.toolsButton ?? (getLanguage() === 'zh' ? '工具' : 'Tools')}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-[600px] max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border-2 border-indigo-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 sm:p-6 flex items-center justify-between rounded-t-2xl z-10">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              {t.ui.toolsButton ?? (getLanguage() === 'zh' ? '工具' : 'Tools')}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-xl sm:text-2xl font-bold w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-all"
              aria-label={t.ui.cancel}
            >
              ×
            </button>
          </div>

          <div className="sticky top-[72px] sm:top-[88px] bg-white border-b-2 border-gray-200 z-10">
            <div className="flex flex-wrap gap-2 px-4 sm:px-6 pt-4 pb-2">
              {tabs.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setSelectedTab(id)}
                  className={`px-4 py-2 rounded-t-lg font-semibold text-sm sm:text-base transition-all ${
                    selectedTab === id
                      ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {selectedTab === 'drawLots' && (
              <div className="space-y-4">
                <section>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    {currentRules.title}
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1.5 list-disc list-inside">
                    <li>{currentRules.rule1}</li>
                    <li>{currentRules.rule2}</li>
                  </ul>
                </section>
                <DrawLots embedded />
              </div>
            )}
            {selectedTab === 'pensieve' && (
              <div className="space-y-4">
                <Pensieve embedded />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
